const BASE = process.env.REACT_APP_API_URL || '';

function getToken() {
  return localStorage.getItem('admin_token') || '';
}

async function request(path, opts = {}) {
  const res = await fetch(`${BASE}/api/admin${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }
  if (!res.ok) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
  return data;
}

// Hits the public (non-admin) API — no admin prefix needed
async function publicRequest(path) {
  const res = await fetch(`${BASE}/api${path}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

const api = {
  login:            (body)            => request('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  getDashboard:     ()                => request('/dashboard'),
  getUsers:         (params)          => request(`/users?${new URLSearchParams(params)}`),
  getUser:          (id)              => request(`/users/${id}`),
  toggleUserStatus: (id, is_active)   => request(`/users/${id}/status`, { method: 'PATCH', body: JSON.stringify({ is_active }) }),
  verifyUser:       (id)              => request(`/users/${id}/verify`, { method: 'PATCH' }),
  setDeviceLimit:   (id, device_limit) => request(`/users/${id}/device-limit`, { method: 'PATCH', body: JSON.stringify({ device_limit }) }),
  deleteUser:       (id)              => request(`/users/${id}`, { method: 'DELETE' }),
  getUserWallet:    (id)              => request(`/users/${id}/wallet`),
  fundWallet:       (id, b)           => request(`/users/${id}/wallet/fund`, { method: 'POST', body: JSON.stringify(b) }),
  adjustWallet:     (id, b)           => request(`/users/${id}/wallet/adjust`, { method: 'POST', body: JSON.stringify(b) }),
  grantSubscription:(id, b)           => request(`/users/${id}/subscriptions`, { method: 'POST', body: JSON.stringify(b) }),
  getUserSubscriptions: (id)          => request(`/users/${id}/subscriptions`),
  cancelSubscription:   (subId)       => request(`/subscriptions/${subId}/cancel`, { method: 'PATCH' }),
  extendSubscription:   (subId, d)    => request(`/subscriptions/${subId}/extend`, { method: 'PATCH', body: JSON.stringify({ days: d }) }),
  getTransactions:  (params)          => request(`/transactions?${new URLSearchParams(params)}`),
  getTransaction:   (id)              => request(`/transactions/${id}`),
  retryTransaction: (id)              => request(`/transactions/${id}/retry`, { method: 'POST' }),
  resolveTransaction: (id, note)      => request(`/transactions/${id}/resolve`, { method: 'POST', body: JSON.stringify({ note }) }),
  getDevices:       (params)          => request(`/devices?${new URLSearchParams(params)}`),
  revokeDevice:     (mac)             => request(`/devices/${mac}/revoke`, { method: 'POST' }),
  authorizeDevice:  (mac, duration_minutes) => request(`/devices/${mac}/authorize`, { method: 'POST', body: JSON.stringify({ duration_minutes }) }),
  getConfig:        ()                => request('/config'),
  updateConfig:     (updates)         => request('/config', { method: 'PATCH', body: JSON.stringify({ updates }) }),

  // ── Analytics — all accept an optional lodge_id ───────────────────────────
  getRevenueAnalytics: (period, lodge_id) => {
    const params = new URLSearchParams({ period });
    if (lodge_id) params.set('lodge_id', lodge_id);
    return request(`/analytics/revenue?${params}`);
  },
  getPlanBreakdown: (lodge_id) => {
    const params = new URLSearchParams();
    if (lodge_id) params.set('lodge_id', lodge_id);
    const qs = params.toString();
    return request(`/analytics/plans${qs ? `?${qs}` : ''}`);
  },
  getPlanSubscribers: (planId, status) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    return request(`/analytics/plans/${planId}/subscribers${qs ? `?${qs}` : ''}`);
  },
  getUserGrowth: (lodge_id) => {
    const params = new URLSearchParams();
    if (lodge_id) params.set('lodge_id', lodge_id);
    const qs = params.toString();
    return request(`/analytics/users${qs ? `?${qs}` : ''}`);
  },

  // ── Lodges — uses public endpoint, no admin auth needed ───────────────────
  getLodges: () => publicRequest('/lodges'),

  getAdminLogs: (params)  => request(`/logs?${new URLSearchParams(params)}`),
  getPlans:     ()        => request('/plans'),
  createPlan:   (body)    => request('/plans', { method: 'POST', body: JSON.stringify(body) }),
  updatePlan:   (id, b)   => request(`/plans/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
  deletePlan:   (id)      => request(`/plans/${id}`, { method: 'DELETE' }),
  togglePlan:   (id)      => request(`/plans/${id}/toggle`, { method: 'PATCH' }),
  getEmailSender: ()      => request('/email/sender'),
  sendEmail:     (body)   => request('/email/send',  { method: 'POST', body: JSON.stringify(body) }),
  sendBulkEmail: (body)   => request('/email/bulk',  { method: 'POST', body: JSON.stringify(body) }),
  regenerateDVA: (id)     => request(`/users/${id}/wallet/regenerate-dva`, { method: 'POST' }),
  getSiteSettings:    ()     => request('/settings'),
  updateSiteSettings: (body) => request('/settings', { method: 'PATCH', body: JSON.stringify(body) }),
  getLodgesAdmin: ()        => request('/lodges'),
  createLodge:   (body)     => request('/lodges', { method: 'POST', body: JSON.stringify(body) }),
  updateLodge:   (id, body) => request(`/lodges/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteLodge:   (id)       => request(`/lodges/${id}`, { method: 'DELETE' }),
  getLodgeDetail: (id, params) => request(`/lodges/${id}?${new URLSearchParams(params)}`),

  // ── Access points → lodge mapping ────────────────────────────────────────
  getAccessPoints:   ()          => request('/access-points'),
  syncAccessPoints:  ()          => request('/access-points/sync', { method: 'POST' }),
  updateAccessPoint: (mac, body) => request(`/access-points/${mac}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // ── Lodge outages ────────────────────────────────────────────────────────
  getOutages:       (params) => request(`/outages?${new URLSearchParams(params)}`),
  getOutage:        (id)     => request(`/outages/${id}`),
  compensateOutage: (id)     => request(`/outages/${id}/compensate`, { method: 'POST', body: JSON.stringify({}) }),
  ignoreOutage:     (id)     => request(`/outages/${id}/ignore`, { method: 'POST', body: JSON.stringify({}) }),

  // ── Free-hour credits ────────────────────────────────────────────────────
  getCredits:   (params)          => request(`/credits?${new URLSearchParams(params)}`),
  grantCredit:  (body)            => request('/credits/grant', { method: 'POST', body: JSON.stringify(body) }),
  revokeCredit: (id)              => request(`/credits/${id}/revoke`, { method: 'POST', body: JSON.stringify({}) }),

  // ── Connection sessions (who held which IP, when) ────────────────────────
  getSessions: (params) => request(`/sessions?${new URLSearchParams(params)}`),

  // ── Flutterwave reconciliation (no dashboard login needed) ───────────────
  lookupPayment: (reference)          => request('/payments/lookup', { method: 'POST', body: JSON.stringify({ reference }) }),
  creditPayment: (reference, user_id) => request('/payments/credit', { method: 'POST', body: JSON.stringify({ reference, user_id }) }),
};

export default api;